import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, Inbox, LoaderCircle, MoreHorizontal, Search } from 'lucide-react';
import { usePeople } from '../../context';

export function Avatar({ name = '', src = '', userId = '', size = 'md', className = '' }) {
  const { findPerson } = usePeople();
  const person = findPerson(userId, name);
  const image = src || person?.avatarData || '';
  const initials = String(name || '?').trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase();
  return <span className={`ui-avatar ui-avatar-${size} ${className}`.trim()} aria-hidden="true">{image ? <img src={image} alt="" /> : (initials || '?')}</span>;
}

export function Breadcrumbs({ items = [] }) {
  return (
    <nav className="ui-breadcrumbs" aria-label="Breadcrumb"><ol>
      {items.map((item, index) => (
        <React.Fragment key={`${item.label}-${index}`}>
          <li>{item.to && index < items.length - 1 ? <Link to={item.to}>{item.label}</Link> : <span aria-current={index === items.length - 1 ? 'page' : undefined}>{item.label}</span>}</li>
          {index < items.length - 1 && <ChevronRight size={13} aria-hidden="true" />}
        </React.Fragment>
      ))}
    </ol></nav>
  );
}

export function PageHeader({ title, description, eyebrow, breadcrumbs, actions, children, className = '' }) {
  return (
    <header className={`ui-page-header ${className}`.trim()}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <div className="ui-page-heading-row">
        <div className="ui-page-heading-copy">
          {eyebrow && <span className="ui-eyebrow">{eyebrow}</span>}
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="ui-page-actions">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export function SearchField({ value, onChange, placeholder = 'Search', label = 'Search', className = '', ...props }) {
  return (
    <label className={`ui-search ${className}`.trim()}>
      <Search size={16} aria-hidden="true" /><span className="sr-only">{label}</span>
      <input type="search" value={value} onChange={onChange} placeholder={placeholder} aria-label={label} {...props} />
    </label>
  );
}

const STATUS_LABELS = {
  active: 'Active', completed: 'Completed', complete: 'Complete', fulfilled: 'Fulfilled',
  in_process: 'In progress', in_progress: 'In progress', pending: 'Pending',
  waiting_for_payment: 'Waiting for payment', approval_needed: 'Awaiting client approval',
  changes_requested: 'Changes requested', upload_requested: 'Upload requested',
  couldnt_complete: "Couldn't complete", unread: 'Unread',
};
const STATUS_TONES = {
  active: 'info', completed: 'success', complete: 'success', fulfilled: 'success',
  in_process: 'warning', in_progress: 'warning', pending: 'warning', waiting_for_payment: 'warning',
  approval_needed: 'warning', changes_requested: 'danger', upload_requested: 'warning',
  couldnt_complete: 'danger', unread: 'brand',
};

export function StatusBadge({ status, label, tone, dot = true, className = '' }) {
  const normalized = String(status || '').toLowerCase();
  const text = label || STATUS_LABELS[normalized] || String(status || 'Unknown').replaceAll('_', ' ');
  const resolvedTone = tone || STATUS_TONES[normalized] || 'neutral';
  return <span className={`ui-status ui-status-${resolvedTone} ${className}`.trim()}>{dot && <span className="ui-status-dot" aria-hidden="true" />}{text}</span>;
}

export function ProgressBar({ value = 0, label, compact = false }) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`ui-progress ${compact ? 'ui-progress-compact' : ''}`.trim()}>
      {label && <div className="ui-progress-label"><span>{label}</span><strong>{safeValue}%</strong></div>}
      <div className="ui-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={safeValue} aria-label={label || 'Progress'}><span style={{ width: `${safeValue}%` }} /></div>
    </div>
  );
}

export function LoadingState({ label = 'Loading workspace…' }) {
  return <div className="ui-state" role="status"><LoaderCircle className="ui-state-spinner" size={22} /><span>{label}</span></div>;
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, compact = false }) {
  return <div className={`ui-state ui-empty-state ${compact ? 'ui-state-compact' : ''}`.trim()}><span className="ui-state-icon"><Icon size={20} /></span><div><strong>{title}</strong>{description && <p>{description}</p>}</div>{action}</div>;
}

export function ErrorState({ message, onRetry }) {
  return <div className="ui-error-state" role="alert"><AlertCircle size={17} /><span>{message || 'Something went wrong.'}</span>{onRetry && <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>Try again</button>}</div>;
}

export function DataTable({ columns, rows, rowKey = 'id', onRowClick, empty, className = '', caption }) {
  return (
    <div className={`ui-table-wrap ${className}`.trim()}>
      <table className="ui-data-table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead><tr>{columns.map((column) => <th key={column.key} className={column.className || ''}>{column.label}</th>)}</tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={typeof rowKey === 'function' ? rowKey(row) : row[rowKey]} className={onRowClick ? 'ui-clickable-row' : ''} tabIndex={onRowClick ? 0 : undefined} onClick={onRowClick ? () => onRowClick(row) : undefined} onKeyDown={onRowClick ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onRowClick(row); } } : undefined}>
            {columns.map((column) => <td key={column.key} data-label={column.label} className={column.cellClassName || ''}>{column.render ? column.render(row) : row[column.key]}</td>)}
          </tr>
        ))}</tbody>
      </table>
      {rows.length === 0 && empty}
    </div>
  );
}

export function OverflowMenu({ items = [], label = 'More actions', align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'mousedown' && (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target))) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);
  useLayoutEffect(() => {
    if (!open) { setMenuPosition(null); return undefined; }
    const positionMenu = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || 180;
      const menuHeight = menuRef.current?.offsetHeight || (items.length * 34 + 10);
      const gap = 5;
      const edge = 8;
      const preferredLeft = align === 'left' ? rect.left : rect.right - menuWidth;
      const left = Math.max(edge, Math.min(preferredLeft, window.innerWidth - menuWidth - edge));
      const fitsBelow = rect.bottom + gap + menuHeight <= window.innerHeight - edge;
      const top = fitsBelow
        ? rect.bottom + gap
        : Math.max(edge, rect.top - gap - menuHeight);
      setMenuPosition({ left, top });
    };
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => { window.removeEventListener('resize', positionMenu); window.removeEventListener('scroll', positionMenu, true); };
  }, [align, items.length, open]);
  if (!items.length) return null;
  return (
    <div className="ui-overflow" ref={rootRef} onClick={(event) => event.stopPropagation()}>
      <button ref={buttonRef} type="button" className="ui-icon-button" aria-label={label} aria-expanded={open} aria-controls={menuId} onClick={() => setOpen((value) => !value)}><MoreHorizontal size={18} /></button>
      {open && createPortal(<div ref={menuRef} className="ui-menu ui-menu-portal" style={menuPosition || { visibility: 'hidden' }} role="menu" id={menuId}>{items.map((item) => (
        <button key={item.label} type="button" role="menuitem" className={item.danger ? 'danger' : ''} disabled={item.disabled} onClick={() => { setOpen(false); item.onClick?.(); }}>{item.icon && <item.icon size={15} />}{item.label}</button>
      ))}</div>, document.body)}
    </div>
  );
}

export function SectionHeader({ title, description, action, count }) {
  return <div className="ui-section-header"><div><h2>{title}{count !== undefined && <span>{count}</span>}</h2>{description && <p>{description}</p>}</div>{action}</div>;
}

export function ActivityItem({ icon: Icon, title, meta, to, tone = 'neutral' }) {
  const content = <><span className={`ui-activity-icon ui-activity-${tone}`}>{Icon && <Icon size={15} />}</span><span className="ui-activity-copy"><strong>{title}</strong>{meta && <small>{meta}</small>}</span>{to && <ChevronRight size={15} className="ui-row-chevron" />}</>;
  return to ? <Link className="ui-activity-item" to={to}>{content}</Link> : <div className="ui-activity-item">{content}</div>;
}

export function Tabs({ items, value, onChange, label = 'Sections' }) {
  return <div className="ui-tabs" role="tablist" aria-label={label}>{items.map((item) => (
    <button key={item.value} type="button" role="tab" aria-selected={value === item.value} className={value === item.value ? 'active' : ''} onClick={() => onChange(item.value)}>{item.icon && <item.icon size={15} />}{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>
  ))}</div>;
}
